// An order moved by an Aldoo admin to another seller who sends it instead
// (aldoo-na/store/order-move): the seller it was placed with cannot send it in
// time, another sells the same goods, the buyer agreed. The admin picks the
// seller, the lines become that seller's products (each line's counterpart is
// found by name, the rest are put in by hand), the delivery goes over as the
// buyer chose it — changed only where the new seller cannot do it, the
// difference said — and the money is shown: paid by card, what goes back to the
// card or what Aldoo covers (refused past its share). Saving places a new order
// for the new seller, as the site places any, and calls the old one off.
import React, { useEffect, useMemo, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { MagnifyingGlassIcon, ExclamationTriangleIcon, ArrowRightIcon, BuildingStorefrontIcon, MapPinIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { describeLine } from "aldoo-ra/Store/lines"
import { money, Thumb } from "./ui"
import { Stepper, Found, refusal } from "./ModifyDialog"

const keyOf = (row) => `${row.pid}|${row.sku || ""}`

/** A delivery as the admin reads it: courier · way · office. */
const deliveryText = (delivery, t) => {
  if (!delivery?.providerId) return t`No delivery`
  const way = { address: t`to the address`, office: t`to an office`, locker: t`to a locker`, pickup: t`pickup` }[delivery.method] || delivery.method
  return [delivery.providerName || delivery.providerId, way, delivery.office?.name].filter(Boolean).join(" · ")
}

/** An address on one line: street, post code and town, country. */
const addressText = (address) => (address ? [address.line1, address.line2, [address.postal_code, address.city].filter(Boolean).join(" "), address.country && address.country !== "BG" ? address.country : ""].map((part) => String(part || "").trim()).filter(Boolean).join(", ") : "")

/** What stands in the way of a delivery, in the dialog's words. */
const problemText = (problem, t) =>
  ({ seller_does_not_offer: t`The new seller does not deliver this way: choose another`, office_needed: t`Choose the office or locker`, not_for_these_products: t`These products cannot go this way` })[problem] || t`This delivery is not available`

/**
 * `order` as the order page has it; `api` the orders api (moveSellers, moveProducts, moveMatch, moveQuote,
 * move); `renderOfficePicker({ isOpen, onClose, option, city, onPick })` the project's office picker;
 * `onClose(done)` with `{ order_id }` once moved.
 */
export default function MoveDialog({ order, t, api, onClose, renderOfficePicker = null, height = null }) {
  const id = order?.orderId || order?._id
  const currency = order?.currency
  const [sellerTerm, setSellerTerm] = useState("")
  const [sellers, setSellers] = useState([])
  const [seller, setSeller] = useState(null)
  // a row per line of the order (its replacement at the new seller, or none yet), and the products put in besides
  const [rows, setRows] = useState([])
  // the row the search picks a replacement for; none: the search adds a product
  const [target, setTarget] = useState(null)
  const searchRef = useRef(null)
  const [term, setTerm] = useState("")
  const [found, setFound] = useState([])
  const [delivery, setDelivery] = useState(null)
  const [officeFor, setOfficeFor] = useState(null)
  const [quote, setQuote] = useState(null)
  const [quoting, setQuoting] = useState(false)
  const [message, setMessage] = useState("")
  // why it is moved: told to the seller it is taken from (their letter, their order)
  const [reason, setReason] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const asked = useRef(0)

  // the sellers, as the admin types (the one it is with left out)
  useEffect(() => {
    if (seller) return
    const timer = setTimeout(async () => setSellers((await api.moveSellers(id, sellerTerm.trim()))?.sellers || []), 250)
    return () => clearTimeout(timer)
  }, [sellerTerm, seller, id, api])

  // a seller picked: a row per line of the order, with its counterpart there proposed (the admin may change it)
  const pick = async (next) => {
    setSeller(next)
    setError("")
    setDelivery(null)
    setTarget(null)
    const res = await api.moveMatch(id, next.id)
    const proposed = new Map((res?.lines || []).map((entry) => [entry.cartIndex, entry]))
    setRows(
      (order.cart || []).map((line, cartIndex) => {
        const entry = proposed.get(cartIndex)
        const variant = (entry?.product?.variants || []).find((v) => v.sku === entry.sku)
        return {
          id: `c${cartIndex}`,
          cartIndex,
          from: { name: describeLine(line, line.name || line.pid), pid: line.pid, thumbnail: line.thumbnail || null, quantity: line.quantity ?? 1 },
          product: entry?.product ? { pid: entry.product.pid, sku: entry.sku || "", name: variant ? `${entry.product.name} (${variant.label})` : entry.product.name, thumbnail: entry.product.thumbnail } : null,
          quantity: line.quantity ?? 1,
        }
      })
    )
  }

  // the seller's products, as the admin types
  useEffect(() => {
    const q = term.trim()
    if (!seller || q.length < 2) return setFound([])
    const timer = setTimeout(async () => setFound((await api.moveProducts(id, seller.id, q))?.products || []), 300)
    return () => clearTimeout(timer)
  }, [term, seller, id, api])

  // a product found: the replacement of the row being chosen for, else one more product (a line of its own)
  const add = (product) => {
    const chosen = { pid: product.pid, sku: product.sku || "", name: product.label, thumbnail: product.thumbnail }
    setRows((list) => {
      if (target) return list.map((row) => (row.id === target ? { ...row, product: chosen, quantity: row.quantity || row.from?.quantity || 1 } : row))
      const same = list.find((row) => !row.from && row.product && keyOf(row.product) === keyOf(chosen))
      if (same) return list.map((row) => (row === same ? { ...row, quantity: row.quantity + 1 } : row))
      return [...list, { id: `n${keyOf(chosen)}`, product: chosen, quantity: 1 }]
    })
    setTarget(null)
    setTerm("")
    setFound([])
  }
  const replace = (row) => {
    setTarget(row.id)
    setTerm(row.from ? row.from.name.replace(/^[^:]{1,20}:\s*/, "").slice(0, 60) : "")
    setTimeout(() => searchRef.current?.focus?.(), 0)
  }

  // the new order quoted as it changes: its lines priced by the seller, the delivery, the money
  const lines = useMemo(() => rows.filter((row) => row.product && row.quantity > 0).map((row) => ({ pid: row.product.pid, ...(row.product.sku ? { sku: row.product.sku } : {}), quantity: row.quantity })), [rows])
  // a line of the order with no replacement chosen and not left out: the move waits for the admin's decision
  const undecided = rows.filter((row) => row.from && !row.product && row.quantity > 0)
  useEffect(() => {
    if (!seller || !lines.length) return setQuote(null)
    const ask = ++asked.current
    setQuoting(true)
    const timer = setTimeout(async () => {
      const res = await api.moveQuote(id, { sellerId: seller.id, lines, delivery })
      if (ask !== asked.current) return
      setQuoting(false)
      setQuote(res)
    }, 300)
    return () => clearTimeout(timer)
  }, [seller, lines, delivery, id, api])

  const priceOf = (row) => row.product && quote?.cart?.find((line) => line.pid === row.product.pid && (line.sku || "") === (row.product.sku || ""))
  const original = order?.shippingProvider || null
  const chosen = quote?.delivery || null
  const deliveryChanged = !!chosen && (chosen.providerId !== original?.providerId || chosen.method !== original?.method || (chosen.office?.id || "") !== (original?.office?.id || ""))
  const options = (quote?.deliveries || []).map((option) => ({ value: `${option.providerId}|${option.method}`, name: `${option.providerName} · ${option.label}${Number.isFinite(option.cost) ? ` · ${money(option.cost, currency)}` : ""}`, option }))
  const m = quote?.money
  const quoteError = quote?.error ? refusal(quote, t) : ""
  const ready = !!seller && !!lines.length && !!quote && !quote.error && !quote.deliveryProblem && !m?.blocked && !quoting && !!reason.trim() && !undecided.length

  const save = async () => {
    setBusy(true)
    setError("")
    const res = await api.move(id, { sellerId: seller.id, lines, delivery, message: message.trim(), reason: reason.trim() })
    setBusy(false)
    if (res?.error) return setError(res.error === "move_blocked" ? t`The new total is more than Aldoo can cover: lower it` : refusal(res, t))
    onClose?.(res)
  }

  // Save asks first: where it goes, what it holds, the delivery, the money
  const confirm = () => {
    if (!ready || busy) return
    showPromptDialog({
      variant: "admin",
      title: t("Move order %s", order?.orderId || ""),
      message: (
        <div className="flex flex-col gap-3 text-sm" data-testid="move-confirm">
          <span>{t("A new order is placed with %s, who gets it as any new order. Order %s is canceled and %s is told not to send it. The buyer is told by email.", seller.name, order?.orderId || "", order?.sellerName || "")}</span>
          <div className="flex flex-col divide-y divide-admin_bordered rounded-md border border-admin_bordered">
            {(quote.cart || []).map((line, index) => (
              <div key={index} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate text-admin_text">{describeLine(line, line.name)}</span>
                <span className="shrink-0 tabular-nums text-admin_text_secondary">{line.quantity} × {money(line.price, currency)}</span>
              </div>
            ))}
          </div>
          <div className={twMerge("text-admin_text", deliveryChanged && "text-admin_warning")}>
            {t`Delivery`}: {deliveryText(chosen, t)}
            {deliveryChanged && <span className="text-admin_text_secondary"> ({t("the buyer chose %s", deliveryText(original, t))})</span>}
          </div>
          <div className="flex justify-between font-semibold text-admin_text">
            <span>{m?.card ? t`New total` : t`To collect on delivery`}</span>
            <span className="tabular-nums">
              <span className="mr-2 font-normal text-admin_text_secondary line-through">{money(order?.amount || 0, currency)}</span>
              {money(quote.total, currency)}
            </span>
          </div>
          {m?.card && m.refund > 0 && <div className="text-admin_text">{t("%s goes back to the buyer's card.", money(m.refund, currency))}</div>}
          {m?.card && m.covered > 0 && <div className="text-admin_warning">{t("Aldoo covers %s.", money(m.covered, currency))}</div>}
          <div className="text-admin_text">
            <span className="text-admin_text_secondary">{t("Reason, told to %s", order?.sellerName || "")}: </span>
            <span className="whitespace-pre-line">{reason.trim()}</span>
          </div>
          {message.trim() && <div className="rounded-md bg-admin_neutral_1/60 px-3 py-2 text-admin_text whitespace-pre-line">{message.trim()}</div>}
        </div>
      ),
      confirmText: t`Move the order`,
      cancelText: t`Back`,
      onConfirm: () => {
        save()
      },
    })
  }

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      variant="admin_compact"
      title={t("Move order %s to another seller", order?.orderId || "")}
      containerStyle={height ? { height: `${Math.round(height)}px` } : undefined}
      footerContent={
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="admin_primary" className="ml-auto" onClick={confirm} disabled={!ready || busy} data-testid="move-submit">
            {busy ? t`Saving…` : t`Move the order`}
          </Button>
        </div>
      }
      styleOverrides={{
        container: "bg-admin_paper border-admin_bordered w-full h-[100dvh] max-h-[100dvh] md:h-auto md:max-w-[900px] md:max-h-[94vh] overflow-hidden rounded-none md:rounded-md border shadow-lg",
        footer: "px-5 py-3 border-t border-admin_bordered bg-admin_paper",
      }}
    >
      <div className="flex flex-col gap-4 p-5 pt-3" data-testid="move-dialog">
        {/* the seller: from the one it is with, to the one chosen */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm text-admin_text">
            <BuildingStorefrontIcon className="w-4 h-4 text-admin_text_secondary" />
            <span className="font-medium">{order?.sellerName}</span>
            <ArrowRightIcon className="w-4 h-4 text-admin_text_secondary" />
            {seller ? (
              <>
                <span className="font-semibold">{seller.name}</span>
                <button type="button" className="text-xs text-admin_primary hover:underline" onClick={() => { setSeller(null); setRows([]); setTarget(null); setQuote(null) }} data-testid="move-change-seller">
                  {t`change`}
                </button>
              </>
            ) : (
              <span className="text-admin_text_secondary">{t`choose the seller`}</span>
            )}
          </div>
          {!seller && (
            <>
              <Input variant="admin" icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />} placeholder={t`Search sellers…`} value={sellerTerm} onChange={(e) => setSellerTerm(e.target.value)} data-testid="move-seller-search" />
              <div className="flex flex-col divide-y divide-admin_bordered rounded-md border border-admin_bordered max-h-72 overflow-y-auto">
                {sellers.map((entry) => (
                  <button key={entry.id} type="button" className="px-3 py-2 text-left text-sm text-admin_text hover:bg-admin_neutral_1" onClick={() => pick(entry)} data-testid="move-seller">
                    {entry.name}
                  </button>
                ))}
                {!sellers.length && <div className="px-3 py-2 text-xs text-admin_text_secondary">{t`No sellers found`}</div>}
              </div>
            </>
          )}
        </div>

        {seller && (
          <>
            {/* the search: a replacement for the row chosen, or one more product */}
            <div className="flex flex-col gap-1">
              {target && (
                <div className="flex items-center gap-2 text-xs text-admin_primary" data-testid="move-target">
                  {t("Replacement for %s", rows.find((row) => row.id === target)?.from?.name || "")}
                  <button type="button" className="text-admin_text_secondary hover:underline" onClick={() => { setTarget(null); setTerm(""); setFound([]) }}>
                    {t`cancel`}
                  </button>
                </div>
              )}
              <Input ref={searchRef} variant="admin" icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />} placeholder={target ? t("Search %s's products…", seller.name) : t("Search %s's products to add…", seller.name)} value={term} onChange={(e) => setTerm(e.target.value)} data-testid="move-search" />
              {found.length > 0 && (
                <div className="flex flex-col divide-y divide-admin_bordered rounded-md border border-admin_bordered px-3 max-h-72 overflow-y-auto">
                  {found.map((product) => (
                    <Found key={product.pid} product={product} t={t} currency={currency} onAdd={add} />
                  ))}
                </div>
              )}
            </div>

            {/* the order's lines, each with what replaces it at the new seller; the products put in besides */}
            <div className="flex flex-col divide-y divide-admin_bordered border-y border-admin_bordered">
              {rows.map((row) => {
                const line = priceOf(row)
                const left = row.from && row.quantity === 0
                return (
                  <div key={row.id} className={twMerge("flex flex-col gap-1.5 py-2.5", target === row.id && "bg-admin_primary/5 -mx-2 px-2 rounded")} data-testid="move-line">
                    {/* what the buyer ordered */}
                    {row.from && (
                      <div className={twMerge("flex items-center gap-2 text-xs text-admin_text_secondary", left && "line-through")}>
                        <Thumb src={row.from.thumbnail} name={row.from.name} className="w-6 h-6 rounded" />
                        <span className="min-w-0 truncate">{row.from.name}</span>
                        <span className="shrink-0">× {row.from.quantity}</span>
                        <ArrowRightIcon className="w-3.5 h-3.5 shrink-0" />
                      </div>
                    )}
                    {/* what the new seller sends instead */}
                    {left ? (
                      <div className="flex items-center gap-3 text-sm text-admin_text_secondary">
                        <span className="flex-1">{t`Left out of the new order`}</span>
                        <Button variant="admin_secondary" onClick={() => setRows((list) => list.map((entry) => (entry === row ? { ...entry, quantity: entry.from.quantity } : entry)))} data-testid="move-put-back">
                          {t`Put it back`}
                        </Button>
                      </div>
                    ) : row.product ? (
                      <div className="flex items-center gap-3">
                        <Thumb src={row.product.thumbnail} name={row.product.name} className="w-10 h-10 rounded" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-admin_text">{row.product.name}</div>
                          <div className="text-xs text-admin_text_secondary break-all">{row.product.pid}</div>
                        </div>
                        <Stepper value={row.quantity} onChange={(quantity) => setRows((list) => list.map((entry) => (entry === row ? { ...entry, quantity } : entry)))} testId="move-quantity" />
                        <div className="w-24 text-right text-sm tabular-nums text-admin_text">{line ? money(Math.round(line.price * row.quantity), currency) : "…"}</div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-admin_warning" data-testid="move-undecided">
                        <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                        {t("Not found at %s: choose a replacement or leave it out", seller.name)}
                      </div>
                    )}
                    {!left && (
                      <div className="flex flex-wrap gap-3 text-xs">
                        <button type="button" className="text-admin_primary hover:underline" onClick={() => replace(row)} data-testid="move-replace">
                          {row.from ? (row.product ? t`Replace with another product` : t`Choose a replacement`) : t`Change the product`}
                        </button>
                        <button
                          type="button"
                          className="text-admin_text_secondary hover:text-admin_error hover:underline"
                          onClick={() => setRows((list) => (row.from ? list.map((entry) => (entry === row ? { ...entry, quantity: 0 } : entry)) : list.filter((entry) => entry !== row)))}
                          data-testid="move-remove"
                        >
                          {row.from ? t`Leave it out` : t`Take it out`}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* the delivery: as the buyer chose it, and what it is with the new seller */}
            {quote && !quote.error && (quote.deliveries?.length > 0 || chosen) && (
              <div className={twMerge("flex flex-col gap-2 rounded-md border p-3 text-sm", deliveryChanged || quote.deliveryProblem ? "border-admin_warning/50 bg-admin_warning/5" : "border-admin_bordered")} data-testid="move-delivery">
                {/* who receives it and where: the delivery is chosen for them (the picker starts at their address) */}
                {quote.buyer && (
                  <div className="flex flex-col gap-0.5 rounded-md bg-admin_paper border border-admin_bordered px-3 py-2" data-testid="move-buyer">
                    <span className="text-xs uppercase tracking-wide text-admin_text_secondary">{t`Recipient`}</span>
                    <span className="font-medium text-admin_text">{quote.buyer.name || quote.buyer.email}</span>
                    {quote.buyer.phone && <span className="text-admin_text_secondary">{quote.buyer.phone}</span>}
                    {addressText(quote.buyer.address) && <span className="text-admin_text">{addressText(quote.buyer.address)}</span>}
                    {!quote.buyer.address?.line1 && original?.office?.name && <span className="text-xs text-admin_text_secondary">{t("No street address: the buyer collects at %s", original.office.name)}</span>}
                  </div>
                )}
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  <div>
                    <span className="text-admin_text_secondary">{t`The buyer chose`}: </span>
                    <span className="text-admin_text">{deliveryText(original, t)}</span>
                    {Number.isFinite(order?.shippingCost) && <span className="text-admin_text_secondary"> · {money(order.shippingCost, currency)}</span>}
                  </div>
                  <div>
                    <span className="text-admin_text_secondary">{t`With the new seller`}: </span>
                    <span className={twMerge("font-medium", deliveryChanged ? "text-admin_warning" : "text-admin_text")}>{deliveryText(chosen, t)}</span>
                    {!quote.deliveryProblem && <span className="text-admin_text_secondary"> · {money(quote.shippingCost || 0, currency)}</span>}
                  </div>
                </div>
                {quote.deliveryProblem && (
                  <div className="flex items-center gap-1.5 text-admin_warning">
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    {problemText(quote.deliveryProblem, t)}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-full sm:w-80">
                    <DropdownSelect
                      variant="admin"
                      placeholder={t`Choose the delivery`}
                      options={options}
                      value={options.filter((option) => chosen && option.value === `${chosen.providerId}|${chosen.method}`)}
                      onChange={(value) => {
                        const option = (Array.isArray(value) ? value[0] : value)?.option
                        if (!option) return
                        // an office or locker way: the office is picked next (the buyer's own kept when it is that courier's)
                        const keepOffice = option.pickup && original?.providerId === option.providerId && original?.method === option.method ? original.office : null
                        setDelivery({ providerId: option.providerId, method: option.method, ...(keepOffice ? { office: keepOffice } : {}) })
                        if (option.pickup && !keepOffice && renderOfficePicker) setOfficeFor(option)
                      }}
                    />
                  </div>
                  {chosen?.providerId && (quote.deliveries || []).find((option) => option.providerId === chosen.providerId && option.method === chosen.method)?.pickup && renderOfficePicker && (
                    <Button variant="admin_secondary" onClick={() => setOfficeFor((quote.deliveries || []).find((option) => option.providerId === chosen.providerId && option.method === chosen.method))} data-testid="move-office">
                      <MapPinIcon className="w-4 h-4 mr-1" />
                      {chosen.office ? t`Change the office` : t`Choose the office`}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* the money: what was charged or is collected, the new total, the difference */}
            {quote && !quote.error && (
              <div className="ml-auto w-full max-w-sm flex flex-col gap-1 text-sm" data-testid="move-money">
                <div className="flex justify-between text-admin_text_secondary">
                  <span>{t`Items`}</span>
                  <span className="tabular-nums">{money(quote.goods || 0, currency)}</span>
                </div>
                {quote.discountAmount > 0 && (
                  <div className="flex justify-between text-admin_text_secondary">
                    <span>{t`Discount`}</span>
                    <span className="tabular-nums">−{money(quote.discountAmount, currency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-admin_text_secondary">
                  <span>{t`Shipping`}</span>
                  <span className="tabular-nums">{money(quote.shippingCost || 0, currency)}</span>
                </div>
                <div className="flex justify-between font-semibold text-admin_text">
                  <span>{m?.card ? t`New total` : t`To collect on delivery`}</span>
                  <span className="tabular-nums">
                    <span className="mr-2 font-normal text-admin_text_secondary line-through">{money(order?.amount || 0, currency)}</span>
                    {money(quote.total, currency)}
                  </span>
                </div>
                {m?.card && (
                  <>
                    <div className="flex justify-between text-admin_text_secondary">
                      <span>{t`Charged to the card`}</span>
                      <span className="tabular-nums">{money(m.paid, currency)}</span>
                    </div>
                    {m.refund > 0 && <div className="text-admin_text">{t("%s goes back to the buyer's card.", money(m.refund, currency))}</div>}
                    {m.covered > 0 && !m.blocked && <div className="text-admin_warning">{t("Aldoo covers %s (up to %s).", money(m.covered, currency), money(m.coverLimit, currency))}</div>}
                    {m.blocked && (
                      <div className="flex items-start gap-1.5 text-admin_error" data-testid="move-blocked">
                        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {t("Move blocked: the new total is %s more than was charged, and Aldoo covers at most %s. Lower it.", money(m.covered, currency), money(m.coverLimit, currency))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <label className="flex flex-col gap-1">
              <span className="text-xs text-admin_text_secondary">
                {t("Why it is moved — told to %s (required)", order?.sellerName || "")}
              </span>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t`E.g. The order was not sent within 3 days of the promised date.`}
                className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
                data-testid="move-reason"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-admin_text_secondary">{t`Message to the buyer`}</span>
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t`E.g. As agreed on the phone, your book is sent by another bookshop.`}
                className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
                data-testid="move-message"
              />
            </label>
          </>
        )}

        {(quoteError || error) && (
          <div className="flex items-start gap-2 rounded-md border border-admin_error/40 bg-admin_error/10 p-3 text-sm text-admin_text" data-testid="move-error">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-admin_error" />
            <span>{error || quoteError}</span>
          </div>
        )}
      </div>

      {officeFor &&
        renderOfficePicker &&
        renderOfficePicker({
          isOpen: true,
          option: { ...officeFor, sellerId: seller?.id },
          city: order?.address?.city || "",
          // the buyer's address on the map: the nearest offices first
          near: quote?.buyer?.near || null,
          onClose: () => setOfficeFor(null),
          onPick: (office) => {
            setDelivery({ providerId: officeFor.providerId, method: officeFor.method, office })
            setOfficeFor(null)
          },
        })}
    </Modal>
  )
}
